import { searchByLabel } from '@witmetrics/utils/arrays';
import { isBlank } from '@witmetrics/utils/strings';
import { getRoundedDateTime } from '@witmetrics/utils/time';
import type { Nullish } from '@witmetrics/utils';
import { buildUserOption } from '@witmetrics/unison-core';
import { API } from '@/api';
import { createProjectTask } from '@/api/utils/tasks';

export const classes = {
  dialog: 'w-[98vw] max-w-[700px] min-w-[350px]',
  body: 'w-full max-h-[80vh] min-h-[100px] pt-2 pb-12 overflow-auto',
  row: 'flex gap-4',
  rowItem: 'flex-1 min-w-0',
  input: 'w-full my-4',
};

type UserOption = { value: number; label: string };

export function getInitialDate(
  date: string | null,
  task?: { dueAt: string } | null
) {
  // Default to now if a value isn't already provided by the task or via app state
  let initialDate = new Date();
  if (task?.dueAt) initialDate = new Date(task.dueAt);
  else if (date) initialDate = new Date(date);
  return new Date(getRoundedDateTime(initialDate.toISOString()));
}

export function validateFields({
  dueAt,
  project,
  assignedTo,
  description,
}: {
  dueAt?: string | Nullish;
  project: any;
  assignedTo: any;
  description: string;
}) {
  let invalidFields = [];
  if (!dueAt) invalidFields.push('dueAt');
  if (isBlank(description)) invalidFields.push('description');
  if (!project) invalidFields.push('project');
  if (!assignedTo) invalidFields.push('assignedTo');
  return invalidFields;
}

export function buildTaskData({
  dueAt,
  userId,
  assignedTo,
  description,
}: {
  dueAt: string;
  userId?: number;
  assignedTo: UserOption;
  description: string;
}) {
  return {
    dueAt,
    userId,
    assignedTo: assignedTo.value,
    description: description.trim(),
  };
}

export async function fetchPracticeUsers(practiceId: number) {
  const users = await API.Practices.fetchPracticeUsers(practiceId, {
    filters: [],
    pagination: { page: 1, pageSize: 100 },
  });
  return users.results.map(({ user }) => buildUserOption(user));
}

export async function fetchTaskTemplates(practiceId: number) {
  const response = await API.Practices.fetchPracticeTaskTemplates(practiceId, {
    filters: [],
    pagination: { page: 1, pageSize: 100 },
  });
  return response.results;
}

export async function searchAssignedTo(
  searchQuery: string,
  practiceUsers: { value: number; label: string }[]
) {
  return searchByLabel(practiceUsers, searchQuery);
}

export async function fetchMatchingProjects(query: string) {
  // NOTE: Ignoring pagination
  const { results } = await API.UnisonProjects.fetchMatchingUnisonProjects({
    search: query,
    fields: [
      'name',
      'externalRef',
      'description',
      'user.firstname',
      'user.lastname',
      'user.externalRef',
    ],
    filters: [],
    pagination: { page: 1, pageSize: 20 },
  });
  return results;
}

export async function createNewTask(
  projectId: number,
  data: ReturnType<typeof buildTaskData>
) {
  return createProjectTask(projectId, data);
}

export function updateTask(
  taskId: number,
  data: ReturnType<typeof buildTaskData>
) {
  return API.Tasks.updateTask(taskId, data);
}
